import { Card, Pointer } from '@yleisradio/areena-types';
import React, { useState } from 'react';
import {
  AudioItem,
  AudioSettingsStorage,
  MultiaudioUIstate,
  StreamComponents,
  SubtitleBackgroundOption,
  SubtitleItem,
  SubtitleSettingsStorage,
  SubtitleSizeOption,
} from '../appTypings';
import {
  audioSettingsStorage,
  subtitleSettingsStorage,
} from '../initialStates';
import { HbbTVPlayer } from './adapters/HbbTVPlayer';
import { Html5Player } from './adapters/HTML5Player';
import { useLocalStorage } from '../hooks/useLocalStorage';

const PlayerContextNotInitialized = 'Player context not initialized';

type PlayerState = {
  currentProgram: Card | undefined;
  nextFromPlaylistSource: Pointer | undefined | null;
  streamComponents: StreamComponents | null;
  multiaudio: {
    selectedSubtitle: SubtitleItem | null;
    selectedAudio: AudioItem | null;
    selectionUIstate: MultiaudioUIstate;
    subtitleBackground: SubtitleBackgroundOption;
    subtitleSize: SubtitleSizeOption;
  };
  subtitleSettingsStorage: SubtitleSettingsStorage;
  audioSettingsStorage: AudioSettingsStorage;
};

interface Value {
  state: PlayerState;
  player: HbbTVPlayer | Html5Player | undefined;
  setPlayer: React.Dispatch<
    React.SetStateAction<HbbTVPlayer | Html5Player | undefined>
  >;
  setStreamComponents: (streamComponents: StreamComponents) => void;
  setSubtitle: (subtitle: SubtitleItem | null) => void;
  setAudio: (audio: AudioItem | null) => void;
  setMultiaudioUiState: (state: MultiaudioUIstate) => void;
  setSubtitleBackground: (background: SubtitleBackgroundOption) => void;
  setSubtitleSize: (size: SubtitleSizeOption) => void;
  setAudioSettingsStorageToState: (
    audioSettingsStorage: AudioSettingsStorage
  ) => void;
  setSubtitleSettingsStorageToState: (
    subtitleSettingsStorage: SubtitleSettingsStorage
  ) => void;
  setCurrentProgram: (program: Card | undefined) => void;
  setNextPointerFromPlaylistSource: (pointer: Pointer | null) => void;
}

const initialState: PlayerState = {
  currentProgram: undefined,
  multiaudio: {
    selectedSubtitle: null,
    selectedAudio: null,
    selectionUIstate: MultiaudioUIstate.main,
    subtitleBackground: 'transparent',
    subtitleSize: 'normal',
  },
  subtitleSettingsStorage: subtitleSettingsStorage,
  audioSettingsStorage: audioSettingsStorage,
  nextFromPlaylistSource: undefined,
  streamComponents: null,
};

export const PlayerContext = React.createContext<Value>({
  state: initialState,
  player: undefined,
  setPlayer: () => {
    console.log(PlayerContextNotInitialized);
  },
  setStreamComponents: () => {
    console.log(PlayerContextNotInitialized);
  },
  setSubtitle: () => {
    console.log(PlayerContextNotInitialized);
  },
  setAudio: () => {
    console.log(PlayerContextNotInitialized);
  },
  setMultiaudioUiState: () => {
    console.log(PlayerContextNotInitialized);
  },
  setSubtitleBackground: () => {
    console.log(PlayerContextNotInitialized);
  },
  setSubtitleSize: () => {
    console.log(PlayerContextNotInitialized);
  },
  setAudioSettingsStorageToState: () => {
    console.log(PlayerContextNotInitialized);
  },
  setSubtitleSettingsStorageToState: () => {
    console.log(PlayerContextNotInitialized);
  },
  setCurrentProgram: () => {
    console.log(PlayerContextNotInitialized);
  },
  setNextPointerFromPlaylistSource: () => {
    console.log(PlayerContextNotInitialized);
  },
});

export const PlayerContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [player, setPlayer] = useState<Html5Player | HbbTVPlayer | undefined>(
    undefined
  );
  const subtitleSettingsInStorage = useLocalStorage<SubtitleSettingsStorage>({
    key: 'subtitleSettings',
  });

  const audioSettingsInStorage = useLocalStorage<AudioSettingsStorage>({
    key: 'audioSettings',
  });

  const [state, setState] = useState<PlayerState>({
    ...initialState,
    subtitleSettingsStorage:
      subtitleSettingsInStorage.getItem() || subtitleSettingsStorage,
    audioSettingsStorage:
      audioSettingsInStorage.getItem() || audioSettingsStorage,
  });

  const value = {
    state,
    player,
    setPlayer,
    setStreamComponents: (streamComponents: StreamComponents) => {
      setState(prevState => ({ ...prevState, streamComponents }));
    },
    setSubtitle: (selectedSubtitle: SubtitleItem | null) => {
      setState(prevState => {
        if (
          prevState.multiaudio.selectedSubtitle?.kind ===
            selectedSubtitle?.kind &&
          selectedSubtitle?.language ===
            prevState.multiaudio.selectedSubtitle?.language &&
          selectedSubtitle?.type === prevState.multiaudio.selectedSubtitle?.type
        )
          return prevState;
        return {
          ...prevState,
          multiaudio: { ...prevState.multiaudio, selectedSubtitle },
        };
      });
    },
    setAudio: (selectedAudio: AudioItem | null) => {
      setState(prevState => {
        if (
          prevState.multiaudio.selectedAudio?.kind === selectedAudio?.kind &&
          prevState.multiaudio.selectedAudio?.language ===
            selectedAudio?.language
        )
          return prevState;
        return {
          ...prevState,
          multiaudio: { ...prevState.multiaudio, selectedAudio },
        };
      });
    },
    setMultiaudioUiState: (selectionUIstate: MultiaudioUIstate) => {
      setState(prevState => ({
        ...prevState,
        multiaudio: { ...prevState.multiaudio, selectionUIstate },
      }));
    },
    setSubtitleSize: (subtitleSize: SubtitleSizeOption) => {
      setState(prevState => ({
        ...prevState,
        multiaudio: { ...prevState.multiaudio, subtitleSize },
      }));
    },
    setSubtitleBackground: (subtitleBackground: SubtitleBackgroundOption) => {
      setState(prevState => ({
        ...prevState,
        multiaudio: { ...prevState.multiaudio, subtitleBackground },
      }));
    },
    setAudioSettingsStorageToState: (
      audioSettingsStorage: AudioSettingsStorage
    ) => {
      setState(prevState => ({
        ...prevState,
        audioSettingsStorage,
      }));
    },
    setSubtitleSettingsStorageToState: (
      subtitleSettingsStorage: SubtitleSettingsStorage
    ) => {
      setState(prevState => ({
        ...prevState,
        subtitleSettingsStorage,
      }));
    },
    setCurrentProgram: (currentProgram: Card | undefined) => {
      setState(prevState => ({
        ...prevState,
        currentProgram,
      }));
    },
    setNextPointerFromPlaylistSource: (
      nextFromPlaylistSource: Pointer | null
    ) => {
      setState(prevState => ({
        ...prevState,
        nextFromPlaylistSource,
      }));
    },
  };

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
};

export function usePlayerContext() {
  return React.useContext(PlayerContext);
}
